import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Users, Briefcase, Clock, Award, Star, Zap, Target, TrendingUp, Code, Cpu, Cloud, Shield, Database, Wifi } from 'lucide-react';
import logoImage from '../data/d.png';
import { Scroll3DSection, Tilt3D } from './Scroll3D';

const About = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const stats = [
    {
      icon: Users,
      value: t('about.stats.clients'),
      label: t('about.statsLabels.clients'),
      color: 'from-neon-green to-neon-cyan',
    },
    {
      icon: Briefcase,
      value: t('about.stats.projects'),
      label: t('about.statsLabels.projects'),
      color: 'from-neon-cyan to-neon-blue',
    },
    {
      icon: Clock,
      value: t('about.stats.years'),
      label: t('about.statsLabels.years'),
      color: 'from-neon-blue to-neon-purple',
    },
    {
      icon: Award,
      value: t('about.stats.team'),
      label: t('about.statsLabels.team'),
      color: 'from-neon-purple to-neon-green',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="about" ref={ref} className="py-10 bg-transparent dark:bg-transparent relative overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-10 w-40 h-40 bg-gradient-to-r from-neon-green/15 to-neon-cyan/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-gradient-to-r from-neon-blue/15 to-neon-purple/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-gradient-to-r from-neon-green/8 to-neon-purple/8 rounded-full blur-3xl"></div>
      </div>

      <Scroll3DSection className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="max-w-7xl mx-auto"
        >
          {/* Enhanced Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center space-x-3 rtl:space-x-reverse bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 backdrop-blur-xl border border-neon-green/40 rounded-full px-8 py-4 mb-8 shadow-2xl shadow-neon-green/20"
            >
              <Star className="w-6 h-6 text-neon-green" />
              <span className="text-neon-green font-bold text-lg">{t('about.badge')}</span>
              <Zap className="w-5 h-5 text-neon-cyan" />
            </motion.div>
            
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-8">
              <span className="gradient-text-neon drop-shadow-2xl">{t('about.title')}</span>
            </h2>
            <p className="text-2xl text-white/80 max-w-4xl mx-auto leading-relaxed font-light">
              {t('about.subtitle')}
            </p>
          </motion.div>

          {/* Enhanced Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Enhanced Text Content */}
            <motion.div variants={itemVariants} className="space-y-8">
              <p className="text-xl text-white/80 leading-relaxed">
                {t('about.description')}
              </p>
              
              {/* Enhanced Features List */}
              <div className="space-y-6">
                {t('about.features', { returnObjects: true }).map((feature, index) => {
                  const icons = [Zap, Users, Target, TrendingUp];
                  const Icon = icons[index % icons.length];
                  return (
                    <motion.div
                      key={index}
                      initial={{ x: -20, opacity: 0 }}
                      animate={isInView ? { x: 0, opacity: 1 } : { x: -20, opacity: 0 }}
                      transition={{ delay: index * 0.1 + 0.5 }}
                      className="flex items-center space-x-4 rtl:space-x-reverse group"
                    >
                      <div className="w-12 h-12 bg-gradient-to-r from-neon-green/20 to-neon-cyan/20 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 text-neon-green" />
                      </div>
                      <span className="text-white/90 text-lg font-semibold group-hover:text-neon-green transition-colors duration-300">{feature}</span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Enhanced Visual Content */}
            <motion.div
              variants={itemVariants}
              className="relative"
            >
              {/* Floating Tech Icons */}
              {[
                { icon: Code, className: 'text-neon-green', style: 'top-0 left-8', delay: 0 },
                { icon: Cpu, className: 'text-neon-cyan', style: 'top-0 right-8', delay: 0.2 },
                { icon: Cloud, className: 'text-neon-blue', style: 'top-1/2 right-0 -translate-y-1/2', delay: 0.4 },
                { icon: Shield, className: 'text-neon-purple', style: 'bottom-0 right-8', delay: 0.6 },
                { icon: Database, className: 'text-neon-green', style: 'bottom-0 left-8', delay: 0.8 },
                { icon: Wifi, className: 'text-neon-cyan', style: 'top-1/2 left-0 -translate-y-1/2', delay: 1.0 },
              ].map(({ icon: Icon, className, style, delay }, idx) => (
                <motion.div
                  key={idx}
                  initial={{ y: 0 }}
                  animate={{ y: [0, -20, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay, ease: 'easeInOut' }}
                  className={`absolute ${style} z-20`}
                >
                  <Icon className={`w-10 h-10 drop-shadow-lg ${className}`} />
                </motion.div>
              ))}
              <Tilt3D className="relative z-10 w-full h-full">
                <div className="bg-gradient-to-br from-neon-green/20 to-neon-cyan/20 backdrop-blur-xl border border-neon-green/30 rounded-3xl p-10 text-white shadow-2xl shadow-neon-green/20 h-full">
                  <div className="text-center">
                    <div className="relative w-40 h-40 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl border-4 border-neon-green/30">
                      <img 
                        src={logoImage} 
                        alt="Saham Holding Logo" 
                        className="w-28 h-28 object-contain"
                      />
                      <div className="absolute -inset-2 bg-gradient-to-r from-neon-green to-neon-cyan rounded-full blur opacity-30"></div>
                    </div>
                    <h3 className="text-3xl font-black mb-6 text-neon-green">{t('about.innovation')}</h3>
                    <p className="text-white/90 text-lg leading-relaxed">
                      {t('about.innovationDesc')}
                    </p>
                  </div>
                </div>
              </Tilt3D>
              {/* Enhanced Background Elements */}
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-gradient-to-br from-neon-purple/20 to-neon-pink/20 rounded-full blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-neon-blue/20 to-neon-cyan/20 rounded-full blur-2xl"></div>
            </motion.div>
          </div>

          {/* Enhanced Statistics */}
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-2 lg:grid-cols-4 gap-10"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0, opacity: 0 }}
                animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                transition={{ delay: index * 0.1 + 0.8, type: "spring", stiffness: 100 }}
                className="text-center group"
              >
                <Tilt3D className="p-8 bg-black/50 backdrop-blur-xl border border-neon-green/30 rounded-3xl hover:border-neon-green/50 shadow-2xl shadow-neon-green/5 transition-colors duration-300">
                  <div className={`w-20 h-20 bg-gradient-to-r ${stat.color} rounded-3xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-2xl`}>
                    <stat.icon className="w-10 h-10 text-black" />
                  </div>
                  <div className="text-4xl font-black text-white mb-3">
                    {stat.value}
                  </div>
                  <div className="text-white/70 font-semibold text-lg">
                    {stat.label}
                  </div>
                </Tilt3D>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </Scroll3DSection>
    </section>
  );
};

export default About; 